import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TRADES, COUNTIES, DURATIONS } from "@/constants/data";
import { MapPin, Clock, Euro, User, ArrowRight, Filter, ShieldCheck, Flame } from "lucide-react";
import StarRating from "@/components/StarRating";

export default function BrowseWorkers() {
  const [params, setParams] = useSearchParams();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  const trade = params.get("trade") || "";
  const county = params.get("county") || "";
  const availability = params.get("availability") || "";

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (trade) q.trade = trade;
    if (county) q.county = county;
    if (availability) q.availability = availability;
    api.get("/workers", { params: q }).then((r) => setWorkers(r.data)).finally(() => setLoading(false));
  }, [trade, county, availability]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np, { replace: true });
  };

  const clear = () => setParams({}, { replace: true });

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-amber-500 text-xs font-bold tracking-[0.3em] uppercase">Crew Directory</div>
          <h1 className="display text-4xl md:text-5xl mt-2">Available Trades</h1>
        </div>
        <div className="text-sm text-neutral-400" data-testid="workers-count">{workers.length} workers</div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        <aside className="md:col-span-3">
          <div className="border border-neutral-800 bg-neutral-900 p-5 sticky top-20">
            <div className="flex items-center gap-2 mb-5">
              <Filter className="w-4 h-4 text-amber-500" />
              <div className="text-xs font-bold uppercase tracking-widest">Filter</div>
            </div>
            <FilterGroup label="Trade" value={trade} options={TRADES} onChange={(v) => setFilter("trade", v)} testid="wfilter-trade" />
            <FilterGroup label="County" value={county} options={COUNTIES} onChange={(v) => setFilter("county", v)} testid="wfilter-county" />
            <FilterGroup label="Availability" value={availability} options={DURATIONS} onChange={(v) => setFilter("availability", v)} testid="wfilter-availability" />
            <button data-testid="wfilter-clear" onClick={clear} className="mt-4 w-full text-xs font-bold uppercase tracking-widest text-neutral-400 hover:text-amber-500 py-2 border border-neutral-800 hover:border-amber-500 transition-colors">Clear</button>
          </div>
        </aside>

        <section className="md:col-span-9">
          {loading ? (
            <div className="text-neutral-500 text-sm uppercase tracking-widest animate-pulse">Loading crew…</div>
          ) : workers.length === 0 ? (
            <div className="border border-neutral-800 p-12 text-center">
              <div className="display text-2xl mb-2">No workers listed yet</div>
              <div className="text-neutral-400 text-sm">Adjust filters, or invite trades you know to join.</div>
            </div>
          ) : (
            <ul className="grid sm:grid-cols-2 gap-px bg-neutral-800 border border-neutral-800" data-testid="workers-list">
              {workers.map((w) => (
                <li key={w.id} className="bg-neutral-950">
                  <Link
                    to={`/workers/${w.id}`}
                    data-testid={`worker-card-${w.id}`}
                    className={`group block p-6 hover:bg-neutral-900 transition-colors h-full ${w.is_boosted ? "border-l-2 border-amber-500 bg-amber-500/5" : (w.worker_verified ? "border-l-2 border-amber-500" : "")}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-neutral-900 border border-neutral-800 group-hover:border-amber-500 grid place-items-center transition-colors">
                        <User className="w-5 h-5 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-xs font-bold uppercase tracking-widest text-amber-500">{w.trade}</div>
                          {w.is_boosted && (
                            <span data-testid={`worker-featured-badge-${w.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-500 text-neutral-950 text-[9px] font-bold uppercase tracking-widest">
                              <Flame className="w-2.5 h-2.5" /> Featured
                            </span>
                          )}
                          {w.worker_verified && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-neutral-800 text-amber-500 text-[9px] font-bold uppercase tracking-widest border border-amber-500/60">
                              <ShieldCheck className="w-2.5 h-2.5" /> Verified
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="display text-2xl group-hover:text-amber-500 transition-colors">{w.headline}</div>
                    <div className="text-xs uppercase tracking-wide text-neutral-400 mt-2">{w.worker_name}</div>
                    <div className="mt-1"><StarRating value={w.worker_avg_rating} count={w.worker_review_count} size={11} /></div>
                    <div className="mt-4 flex flex-wrap gap-3 text-xs uppercase tracking-wide text-neutral-400">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{w.county}{w.counties && w.counties.length > 0 && <span className="text-amber-500 font-bold ml-1">+{w.counties.length}</span>}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{w.availability}</span>
                      <span>{w.years_experience}yr exp</span>
                    </div>
                    <p className="text-sm text-neutral-400 mt-3 line-clamp-2">{w.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      {w.day_rate ? (
                        <div className="display text-2xl text-amber-500 flex items-center"><Euro className="w-4 h-4" />{w.day_rate}<span className="text-xs text-neutral-500 ml-1">/day</span></div>
                      ) : <div className="text-xs uppercase tracking-widest text-neutral-500">Rate: DOE</div>}
                      <div className="inline-flex items-center gap-1 text-xs font-bold uppercase text-neutral-400 group-hover:text-amber-500">View <ArrowRight className="w-3 h-3" /></div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function FilterGroup({ label, value, options, onChange, testid }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              data-testid={`${testid}-${o.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => onChange(active ? "" : o)}
              className={`text-xs px-2.5 py-1.5 uppercase tracking-wide border transition-colors ${active ? "bg-amber-500 text-neutral-950 border-amber-500" : "border-neutral-800 text-neutral-300 hover:border-amber-500 hover:text-amber-500"}`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
